import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { SEOHead, generateLocalBusinessSchema, generateWebsiteSchema } from "@/components/seo/SEOHead";
import { Skeleton } from "@/components/ui/skeleton";
import { LazySection } from "@/components/shared/LazySection";
import { Phone } from "lucide-react";
import { useEnquiryPopup } from "@/contexts/EnquiryPopupContext";

// Import home section components
import { WhoWeServe } from "@/components/home/WhoWeServe";
import { HowItWorks } from "@/components/home/HowItWorks";
import { CoreServices } from "@/components/home/CoreServices";
import { TrustSection } from "@/components/home/TrustSection";
import { FinalCTA } from "@/components/home/FinalCTA";
import { BlogPreview } from "@/components/home/BlogPreview";
import { FloatingCTA } from "@/components/home/FloatingCTA";
import { BudgetCalculator } from "@/components/home/BudgetCalculator";
import { GoogleReviewsSection } from "@/components/home/GoogleReviewsSection";

// Lazy load components
const EnquiryForm = lazy(() => import("@/components/shared/EnquiryForm").then(m => ({ default: m.EnquiryForm })));
const DestinationCard = lazy(() => import("@/components/shared/DestinationCard").then(m => ({ default: m.DestinationCard })));

// Import images
import kashmirImage from "@/assets/kashmir-dal-lake.jpg";
import dubaiImage from "@/assets/dubai-skyline.jpg";
import baliImage from "@/assets/bali-landscape.jpg";
import honeymoonImage from "@/assets/honeymoon-beach.jpg";

// Hero images - first slide uses public URL to match preload hint in index.html (LCP optimization)
const heroMaldives = "/images/hero-maldives.jpg"; // Matches <link rel="preload"> in index.html
import heroKashmir from "@/assets/hero/hero-kashmir.jpg";
import heroDubai from "@/assets/hero/hero-dubai.jpg";
import heroFamily from "@/assets/hero/hero-family.jpg";
import heroRajasthan from "@/assets/hero/hero-rajasthan.jpg";

const HERO_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect fill='%231a365d' width='1920' height='1080'/%3E%3C/svg%3E";

const CardSkeleton = () => (
  <div className="luxury-card p-6 space-y-4">
    <Skeleton className="h-48 w-full rounded-xl" />
    <Skeleton className="h-6 w-3/4" />
    <Skeleton className="h-4 w-full" />
  </div>
);

const Index = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({ 0: true });
  const { openWithDestination } = useEnquiryPopup();

  const heroSlides = [
    { 
      image: heroMaldives, 
      alt: "Maldives honeymoon package from Surat - luxury overwater villa",
      sizes: "(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1440px"
    },
    { image: heroKashmir, alt: "Kashmir tour package from Surat - snow-capped mountains and Dal Lake" },
    { image: heroDubai, alt: "Dubai holiday package from Surat - stunning city skyline" },
    { image: heroRajasthan, alt: "Rajasthan heritage tour package from Surat - Udaipur city palace at sunset" },
    { image: heroFamily, alt: "Family vacation package from Surat travel agency" },
  ];

  const handleImageLoad = (index: number) => {
    setImagesLoaded(prev => ({ ...prev, [index]: true }));
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = HERO_PLACEHOLDER;
  };

  // Preload next hero image
  useEffect(() => {
    const nextIndex = (currentSlide + 1) % heroSlides.length;
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    link.href = heroSlides[nextIndex].image;
    document.head.appendChild(link);
    return () => { if (link.parentNode) link.parentNode.removeChild(link); };
  }, [currentSlide, heroSlides]);

  // Auto-slide timer
  useEffect(() => {
    const timer = setInterval(() => {
      setHasInteracted(true);
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const destinations = [
    { image: kashmirImage, name: "Kashmir", description: "Paradise on Earth with serene valleys and snow-capped mountains.", type: "domestic" as const, alt: "Kashmir tour package from Surat - Dal Lake and mountains" },
    { image: dubaiImage, name: "Dubai", description: "Luxury shopping, modern architecture, and desert adventures.", type: "international" as const, alt: "Dubai holiday package from Surat - Burj Khalifa skyline" },
    { image: baliImage, name: "Bali", description: "Tropical paradise with temples, rice terraces, and beaches.", type: "international" as const, alt: "Bali vacation package from Surat - tropical beaches" },
    { image: honeymoonImage, name: "Maldives", description: "Overwater villas and crystal-clear lagoons.", type: "international" as const, alt: "Maldives honeymoon package from Surat - overwater villa" },
  ];

  // Handler for Budget Calculator's "Get Detailed Itinerary" button
  const handleRequestItinerary = (destination: string) => {
    openWithDestination(destination);
  };

  return (
    <>
      <SEOHead
        title="Best Travel Agency in Surat | Tour Packages"
        description="Plan your dream vacation with Feel Free Holidays, the best travel agency in Surat. Experts in custom international tours, visa assistance, and honeymoon packages. Located in Adajan."
        keywords="Travel Agency Surat, Tour Operators Surat, Visa Consultant Adajan, Honeymoon Packages, Premium Travel Gujarat, feel free holidays"
        structuredData={[generateLocalBusinessSchema(), generateWebsiteSchema()]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          "name": "Feel Free Holidays",
          "url": "https://www.feelfreeholidays.com",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "127",
            "bestRating": "5",
            "worstRating": "1"
          },
          "review": [
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Priya Mehta" },
              "reviewRating": { "@type": "Rating", "ratingValue": "5" },
              "reviewBody": "Our Maldives honeymoon was absolutely magical! Feel Free Holidays took care of every detail. Highly recommended for honeymooners!"
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Amit Shah" },
              "reviewRating": { "@type": "Rating", "ratingValue": "5" },
              "reviewBody": "We had an incredible family vacation in Kashmir! The Dal Lake Shikara ride and Gulmarg cable car were highlights. Excellent planning by Feel Free Holidays."
            }
          ]
        })}</script>
      </Helmet>

      {/* Floating CTA */}
      <FloatingCTA />

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative h-[90vh] min-h-[560px] max-h-[900px] flex items-center justify-center overflow-hidden bg-secondary">
          {heroSlides.map((slide, index) => (
            <picture key={index}>
              <img
                src={slide.image}
                alt={slide.alt}
                width={1920}
                height={1080}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: index === currentSlide ? 1 : 0,
                  transition: hasInteracted ? 'opacity 1000ms ease-in-out' : 'none',
                  zIndex: index === currentSlide ? 1 : 0
                }}
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
                fetchPriority={index === 0 ? "high" : "low"}
                onLoad={() => handleImageLoad(index)}
                onError={handleImageError}
              />
            </picture>
          ))}
          <div className="absolute inset-0 hero-overlay" style={{ zIndex: 2 }} />

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-1.5 z-10" style={{ zIndex: 3 }}>
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => { setHasInteracted(true); setCurrentSlide(index); }}
                className="p-1.5 group"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span className={`block rounded-full transition-all duration-300 ${
                  index === currentSlide ? "bg-white w-10 h-2.5" : "bg-white/40 w-2.5 h-2.5 group-hover:bg-white/70"
                }`} />
              </button>
            ))}
          </div>

          <div className="relative z-10 container-luxury text-center text-white px-5" style={{ zIndex: 3 }}>
            <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
              <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[3.5rem] leading-[1.15] animate-fade-in drop-shadow-lg text-balance">
                Stress-Free Travel, Tickets & Tours — With Real Human Support
              </h1>
              <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto text-white/90 leading-relaxed animate-fade-in-delay-1 drop-shadow-md">
                As the best travel agency in Surat, we offer personalized travel planning with real human support every step of the way.
              </p>
              
              <div className="flex flex-row items-center justify-center gap-4 pt-2 animate-fade-in-delay-2 flex-wrap">
                <Button asChild className="text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-[52px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-lg hover:shadow-xl transition-all">
                  <NavLink to="/contact">
                    <Phone className="mr-2 h-4 w-4" />
                    Plan My Trip
                  </NavLink>
                </Button>
                <Button asChild className="text-sm sm:text-base px-6 sm:px-8 h-12 sm:h-[52px] bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white border border-white/30 rounded-full font-semibold shadow-lg transition-all">
                  <a href="tel:+917490019959">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Our Expert
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <WhoWeServe />

        {/* How It Works */}
        <HowItWorks />

        {/* Core Services */}
        <CoreServices />

        {/* Budget Calculator */}
        <BudgetCalculator onRequestItinerary={handleRequestItinerary} />

        {/* Google Reviews */}
        <GoogleReviewsSection />

        {/* Featured Destinations */}
        <LazySection fallbackHeight="400px" rootMargin="100px">
          <section id="popular-packages" className="section-padding bg-muted/30" aria-labelledby="destinations-heading">
            <div className="container-luxury">
              <div className="text-center space-y-4 mb-14">
                <span className="section-badge">Explore The World</span>
                <h2 id="destinations-heading" className="section-title">Featured Destinations</h2>
                <p className="section-subtitle">Handpicked destinations from serene valleys to tropical paradises.</p>
              </div>
              <Suspense fallback={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">{[...Array(4)].map((_, i) => <CardSkeleton key={i} />)}</div>}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                  {destinations.map((dest, i) => <DestinationCard key={i} {...dest} />)}
                </div>
              </Suspense>
              <div className="text-center mt-14">
                <Button size="lg" variant="outline" asChild className="px-8 rounded-full h-12 font-semibold">
                  <NavLink to="/destinations">View All Destinations</NavLink>
                </Button>
              </div>
            </div>
          </section>
        </LazySection>

        {/* Trust Section */}
        <TrustSection />

        {/* Blog Preview */}
        <BlogPreview />

        {/* Enquiry Form */}
        <LazySection fallbackHeight="400px" rootMargin="100px">
          <section className="section-padding bg-background" aria-labelledby="enquiry-heading">
            <div className="container-luxury max-w-3xl">
              <div className="text-center space-y-4 mb-14">
                <span className="section-badge">Get Started</span>
                <h2 id="enquiry-heading" className="section-title">Plan Your Dream Vacation</h2>
                <p className="section-subtitle">Tell us about your travel aspirations and let our experts craft the perfect journey.</p>
              </div>
              <Suspense fallback={<div className="luxury-card p-8 space-y-6"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-32 w-full" /></div>}>
                <EnquiryForm />
              </Suspense>
            </div>
          </section>
        </LazySection>

        {/* Final CTA */}
        <FinalCTA />
      </div>
    </>
  );
};

export default Index;